import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import {
    BigNumberBox,
    PrimaryButton,
    ProductCardLayout,
    BigNumberFormatValues,
    getDesignationMessage, PROVINCE,
} from '@ratehub/base-ui';
import { trackHeapEventForDepositsAndNavigate, DEPOSITS_VERTICALS } from '@ratehub/inv-common';

import SavingsAccountPropType from '../definitions/SavingsAccountPropType';
import DepositsProductName from '../components/DepositsProductName';
import useSavingsInterestCalculationCallback from '../hooks/useSavingsInterestCalculationCallback';
import SavingsAccountFooter from './SavingsAccountFooter';
import ProductListSavingsDetails from './ProductListSavingsDetails';


function ProductCardLayoutSavings({
    savingsAccount,
    monthlyBalance,
    provinceCode,
    ...otherProps
}) {
    const showCalculationModalCallback = useSavingsInterestCalculationCallback();

    const productCard = getProductCardLayoutPropsForSavings({
        savingsAccount,
        monthlyBalance,
        provinceCode,
        bigNumberClick: () => showCalculationModalCallback({ savingsAccount, monthlyBalance }),
    });

    return (
        <ProductCardLayout
            image={productCard.image}
            name={productCard.name}
            topRightElement={(
                <BigNumberBox
                    {...productCard.bigNumber}
                />
            )}

            details={productCard.details}
            rightOfDetailsElement={productCard.callToAction}

            footer={productCard.footer}
            designationMessage={productCard.designationMessage}
            messageBanner={productCard.messageBanner}
            analyticsObject={productCard.analyticsObject}
            {...otherProps}
        >
            {productCard.children}
        </ProductCardLayout>
    );
}

ProductCardLayoutSavings.propTypes = {
    savingsAccount: SavingsAccountPropType.isRequired,
    monthlyBalance: PropTypes.number.isRequired,
    provinceCode: PropTypes.oneOf(Object.values(PROVINCE)),
};


function getProductCardLayoutPropsForSavings({ savingsAccount, monthlyBalance, provinceCode, bigNumberClick }) {
    return {
        designationMessage: getDesignationMessage({
            isSponsored: savingsAccount.isSponsored,
            isMonetized: savingsAccount.isMonetized,
        }),

        name: {
            Component: (
                <DepositsProductName
                    name={savingsAccount.name}
                    provider={savingsAccount.provider}
                    isPFAWinner={savingsAccount.isPFAWinner}
                    onClick={
                        savingsAccount.isMonetized
                            ? () => trackHeapEventForDepositsAndNavigate({
                                product: savingsAccount,
                                shouldNavigate: true,
                                vertical: DEPOSITS_VERTICALS.SAVINGS,
                            })
                            : undefined}
                    isNameOnNewLine={true}
                />
            ),
        },

        bigNumber: getBigNumberMessages({
            isMonetized: !savingsAccount.isSponsored && savingsAccount.isMonetized,
            interestRate: savingsAccount.interestRate,
            firstYearReturn: savingsAccount.firstYearReturn,
            monthlyBalance: monthlyBalance,
            onClick: bigNumberClick,
        }),

        details: (
            <ProductListSavingsDetails savingsAccount={savingsAccount} />
        ),

        // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
        callToAction: savingsAccount.isMonetized
            ? (
                <PrimaryButton
                    size="small"
                    message={(
                        <FormattedMessage
                            id="web-components.savings-account-cta"
                            defaultMessage="Get this rate"
                        />
                    )}
                    onClick={() => trackHeapEventForDepositsAndNavigate({
                        product: savingsAccount,
                        shouldNavigate: true,
                        provinceCode,
                        vertical: DEPOSITS_VERTICALS.SAVINGS,
                    })}
                    data-name="cta-click-sav"
                />
            )
            : undefined,

        footer: (
            <SavingsAccountFooter
                product={savingsAccount}
                id={savingsAccount.name}
            />
        ),

        analyticsObject: savingsAccount,
    };
}


function getBigNumberMessages({
    interestRate,
    firstYearReturn,
    monthlyBalance,
    onClick,
}) {
    const otherMessages = [
        {
            message: (
                <FormattedMessage
                    key="big-number-1st-year"
                    id="web-components.savings.big-number.first-year-return"
                    defaultMessage="<b>{firstYearReturn, number, CAD0}</b> first year return based on"
                    values={{ firstYearReturn: firstYearReturn }}
                />
            ),
        },
        {
            message: (
                <FormattedMessage
                    key="big-number-balance"
                    id="web-components.savings.big-number.balance"
                    defaultMessage="<b>{balance, number, CAD0}</b> balance"
                    values={{ balance: monthlyBalance }}
                />
            ),
        },
    ];


    return {
        message: (
            <FormattedMessage
                key="big-number-title"
                id="web-components.savings.big-number.interest"
                defaultMessage="<bigger>{interestRate, number, percent2}</bigger>"
                values={{
                    interestRate: interestRate / 100,
                    ...BigNumberFormatValues,
                }}
            />
        ),

        subMessage: (
            <FormattedMessage
                key="big-number-sub-message"
                id="web-components.savings.big-number.sub-message"
                defaultMessage="interest rate"
            />
        ),

        otherMessages: otherMessages,

        isHelpButtonWithOtherMessages: true,
        onClick: onClick,
    };
}


export default ProductCardLayoutSavings;
